import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import {
    Form,
    Formik,
    FormikProps
} from "../../../lib/formik/Formik";
import {
    useEffect,
    useRef,
    useState
} from "react";
import * as yup from 'yup';
import ClientDetailsForm from "./clientDetailsForm";
import {
    baseInitialValues,
    baseValidations
} from "./baseValues";
import OrderDetailsForm from "./orderDetailsForm";
import SettlementDetailsForm, { SETTLEMENT_BANK_SOURCE_LIST } from "./settlementDetailsForm";
import LoadingButton from '@mui/lab/LoadingButton';
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import {
    addOrderEntry,
    getAdvisoryBankDetails,
    getBankBalance,
    getBondsDpId,
    getCounterPartyList,
    getHoldingCommitmentInfo,
    getNonMfBrokersList,
    getNonMfHoldingsList,
    getNonMfSecuritiesList,
    getOrderEntryBanksList,
    getOrderEntryInvestorList,
    getSettlementDetails,
    getTradeCalculation
} from "../../../redux-store/actions/orderManagement";
import {
    AddOrderEntryResponseBody,
    GetAdvisoryBankDetailsResponseBody,
    GetBankBalanceResponseBody,
    GetBondsDpIdResponseBody,
    GetCounterPartyListResponseBody,
    GetHoldingCommitmentInfoResponseBody,
    GetNonMfBrokersListResponseBody,
    GetOrderEntryBanksListResponseBody,
    GetOrderEntryInvestorListResponseBody,
    GetSettlementDetailsResponseBody,
    GetTradeCalculationResponseBody,
    TradeCalculationData,
} from "../../../redux-store/types/orderManagement";
import { showError } from "../../../redux-store/actions/auth";
import OrderSupportingDocumentForm from "./orderSupportingDocumentForm";
import {
    FailureIcon,
    SuccessIcon
} from "../../../assets/SvgIcons";
import { useHistory } from "react-router";
import dayjs from "../../../utils/dayjs";
import {
    MPOWER_ACCOUNT_TYPE_MASTER,
    ORDER_ENTRY_SETTLEMENT_METHOD_CODE,
    PRICE_TYPE_DROPDOWN_LIST,
    UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE
} from "../../../utils/constant";

type ProductType = "Unlisted Equity" | "AIF" | "PMS" | "Bonds" | "Margin Money" | "Corpus In/Out" | "Fee Payment";

interface PortfolioDetails {
    portfolioName: string;
    portfolioId: string;
    accountType: string;
    email: string;
    exchange: string;
    modeOfHolding: string;
    portfolioCcy: string;
    portfolioNo: number;
}

const productTypeList = [
    {
        productLabel: "Unlisted Equity",
        productValue: "Unlisted Equity"
    },
    {
        productLabel: "AIF",
        productValue: "AIF"
    },
    {
        productLabel: "PMS",
        productValue: "PMS"
    },
    {
        productLabel: "Bonds",
        productValue: "Bonds"
    },
    {
        productLabel: "Margin Money",
        productValue: "Margin Money"
    },
    {
        productLabel: "Corpus In/Out",
        productValue: "Corpus In/Out"
    },
    {
        productLabel: "Fee Payment",
        productValue: "Fee Payment"
    }
]

const currentDate = dayjs();

const getTMinus5OrderDateValidation = (businessDate: string) =>
    yup
        .date()
        .required("Order Date is required")
        .test(
            "order-date-max",
            `Order Date cannot be greater than ${businessDate}`,
            function (value) {
                // NOTE: The upper-bound (businessDate) applies to ALL account types including held-away.
                // Only the lower-bound (T-5) is removed for held-away accounts (see order-date-min-non-heldaway).
                if (!value) return true

                const maxDate = dayjs(businessDate, "DD/MM/YYYY").startOf('day')
                const selectedDate = dayjs(value).startOf('day')

                return selectedDate.isSameOrBefore(maxDate)
            }
        )
        .test(
            "order-date-min-non-heldaway",
            `Order Date cannot be less than ${UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE} days from ${businessDate}`,
            function (value) {
                const values = this.parent

                const isHeldAwayAccountType = [MPOWER_ACCOUNT_TYPE_MASTER["HLDAWY"]].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${values?.accountType ?? ""}`.toUpperCase()])

                // NOTE: For held-away accounts, the T-5 lower-bound is intentionally removed
                // to allow recording of historical transactions. The upper-bound (businessDate)
                // still applies via the order-date-max test above.
                if (isHeldAwayAccountType) return true

                if (!value) return true

                const minDate = dayjs(businessDate, "DD/MM/YYYY").startOf('day').subtract(UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE, 'day')
                const selectedDate = dayjs(value).startOf('day')

                return selectedDate.isSameOrAfter(minDate)
            }
        );

// For Product Type = Margin Money/ Corpus In/Out /Fee Payment, if Account Type is Non-ADVISORY/Non-POA, we need to display a popup. This has been done for below scenarios
// 1. When user select client name in product types other than Margin Money/ Corpus In/Out /Fee Payment, the account type is autopopulated, then when the user goes to Margin Money/ Corpus In/Out /Fee Payment and account type is Non-ADVISORY/Non-POA, show popup (done in fxn => handleProductChange)
// 2. When user directly goes to Margin Money/ Corpus In/Out /Fee Payment, and selects/changes the client name, the account type is auto populated, if the account type is Non-ADVISORY/Non-POA, show popup (done in fxn => handleClientNameChange)
// 3. When the user selects/changes the account dropdown in Margin Money/ Corpus In/Out /Fee Payment, the account type is auto populated, if the account type is Non-ADVISORY/Non-POA, show popup (done in fxn => handleAccountChange) 

export default function OrderEntry(): JSX.Element {
    const dispatch = useDispatch();
    const history = useHistory();

    const [initialValues, setInitialValues] = useState<Record<string, any>>(baseInitialValues["Unlisted Equity"])
    const [validations, setValidations] = useState<Record<string, any>>(baseValidations["Unlisted Equity"])

    const [activeProductType, setActiveProductType] = useState<ProductType>("Unlisted Equity")
    const [investorData, setInvestorData] = useState<GetOrderEntryInvestorListResponseBody>({} as GetOrderEntryInvestorListResponseBody)
    const [bankAccountList, setBankAccountList] = useState<GetOrderEntryBanksListResponseBody>({} as GetOrderEntryBanksListResponseBody)
    const [securitiesData, setSecuritiesData] = useState<Record<string, any>>({})
    const [nonMfBrokersList, setNonMfBrokersList] = useState<GetNonMfBrokersListResponseBody>({} as GetNonMfBrokersListResponseBody)
    const [settlementDetailsList, setSettlementDetailsList] = useState<GetSettlementDetailsResponseBody>({} as GetSettlementDetailsResponseBody)
    const [counterPartyList, setCounterPartyList] = useState<GetCounterPartyListResponseBody>({} as GetCounterPartyListResponseBody)

    const [loadingStatus, setLoadingStatus] = useState<
        "apiLoading" |
        "formSubmitting" |
        ""
    >("")
    const [formSubmitStatus, setFormSubmitStatus] = useState<
        "formSubmitSuccess" |
        "formSubmitError" |
        "">("")
    const [formSubmitStatusObj, setFormSubmitStatusObj] = useState<{
        isSuccess: boolean;
        message: string;
    }>({} as {
        isSuccess: boolean;
        message: string;
    })
    const [isDisableTransactDialogVisible, setDisableTransactDialogVisible] = useState<boolean>(false)
    const [uploadedFileArray, setUploadedFileArray] = useState<Array<any>>([[]])

    const formikRef = useRef<FormikProps<Record<string, any>>>(null);

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);
    const { businessDate } = useSelector((store: RootStateType) => store.navbarInfo);
    const { userDetails = {} } = useSelector((store: RootStateType) => store.auth);
    const { locale = "en-IN" } = useSelector((store: RootStateType) => store.currencyInfo);

    const fetchInvestorData = async () => {
        try {
            setLoadingStatus("apiLoading")

            const payload = {
                rmId: `${iwappRmId}`
            }

            const response = await (dispatch(getOrderEntryInvestorList(payload)) as unknown as Promise<GetOrderEntryInvestorListResponseBody>);

            if (response?.isSuccess) {
                setInvestorData(response ?? {})
            }

            if (!response?.isSuccess) {
                dispatch(showError(response?.message ?? ""))
            }
        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setLoadingStatus("")
        }
    }

    useEffect(() => {
        fetchInvestorData()
    }, [])

    useEffect(() => {
        if (businessDate) {
            setInitialValues(prevState => ({
                ...prevState,
                orderDate: dayjs(businessDate, 'DD/MM/YYYY'),
                settlementDate: dayjs(businessDate, 'DD/MM/YYYY'),
            }))

            // NOTE (CORR-26): activeProductType is intentionally NOT in the dependency array.
            // orderDate validation IS recomputed on product change via handleProductChange → setValidations.
            // This effect only handles the businessDate change case — activeProductType is read from
            // closure here which is stale, but handleProductChange already covers product-switch correctly.
            setValidations(prevState => ({
                ...prevState,
                orderDate: ["Unlisted Equity", "Bonds"].includes(activeProductType)
                    ? getTMinus5OrderDateValidation(businessDate)
                    : yup
                        .date()
                        .required("Order Date is required")
                        .max(dayjs(businessDate, "DD/MM/YYYY").toDate(), `Order Date cannot be greater than ${businessDate}`),
                settlementDate: yup
                    .date()
                    .required("Settlement Date is required")
                    .min(dayjs(businessDate, "DD/MM/YYYY").toDate(), `Settlement Date cannot be less than ${businessDate}`),
            }))
        }
    }, [businessDate])

    // The fetchBondsDpId function needs to be called in below scenarios =>
    // 1. On clicking on Bonds radio button, if Account(portfolioId) is already selected them call the api with already selected portfolioId, if not selected DO NOT CALL the api 
    // 2. In Bonds call the api in case of change of portfolioId 
    // 3. If in Bonds if we select the client name there or change the client name, there also call the api
    const fetchBondsDpId = async (selectedPortfolioId: string): Promise<string> => {
        try {
            setLoadingStatus("apiLoading")

            const payload = {
                // TEMP CHANGES
                // portfolioId: "SNIGDHA_WEALT"
                // TEMP CHANGES

                portfolioId: selectedPortfolioId
            }

            const response = await (dispatch(getBondsDpId(payload)) as unknown as Promise<GetBondsDpIdResponseBody>);

            if (response?.isSuccess) {
                return response?.dpId ?? ""
            } else {
                dispatch(showError(response?.message ?? ""))
                return ""
            }
        } catch (error) {
            console.log((error as Error)?.message ?? "");
            return ""
        } finally {
            setLoadingStatus("")
        }
    }

    const fetchSecuritiesData = async (searchInput: string) => {
        try {
            if (searchInput?.trim()?.length !== 0) {

                // If the searchInput matches the full label of an existing security option (securityName + ISIN),
                // send only the securityName — avoids sending "(ISIN)" in the payload after a user selects a security.
                const existingMatch = (formikRef?.current?.values?.security?.securityName &&
                    searchInput?.trim().startsWith(formikRef?.current?.values?.security?.securityName))
                    ? formikRef?.current?.values?.security?.securityName
                    : searchInput?.trim()

                const nonMfHoldingsListPayload = {
                    product: activeProductType === "Unlisted Equity" ? "unListedEquity" : activeProductType?.toLowerCase(),
                    // TEMP HARDCODED PAYLOAD FOR TESTING
                    // portfolioId: activeProductType === "Bonds" ? "All" : "GOPALSEL",
                    // TEMP HARDCODED PAYLOAD FOR TESTING

                    portfolioId: formikRef?.current?.values?.account ?? "",
                    searchInput: existingMatch
                }

                const nonMfSecurityListPayload = {
                    product: activeProductType === "Unlisted Equity" ? "unListedEquity" : activeProductType?.toLowerCase(),
                    searchInput: existingMatch
                }

                if (["Sell", "Redemption", "Drawdown Contribution", "Top Up", "STP Purchase", "Withdrawal"].includes(formikRef?.current?.values?.transactionType ?? "")) {
                    const response = await (dispatch(getNonMfHoldingsList(nonMfHoldingsListPayload)) as unknown as Promise<Record<string, any>>);

                    if (!response?.isSuccess) {
                        dispatch(showError(response?.message ?? "No Data Available"))
                    }

                    if (response?.isSuccess) {
                        setSecuritiesData(response)
                    }
                } else {
                    const response = await (dispatch(getNonMfSecuritiesList(nonMfSecurityListPayload)) as unknown as Promise<Record<string, any>>);

                    setSecuritiesData(response)
                }
            } else {
                setSecuritiesData({})
            }
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        }
    }

    const fetchNonMfBrokers = async (searchInput: string) => {
        try {
            if (searchInput?.trim()?.length !== 0) {
                const payload = {
                    searchInput: searchInput?.trim()
                }

                const response = await (dispatch(getNonMfBrokersList(payload)) as unknown as Promise<GetNonMfBrokersListResponseBody>);

                setNonMfBrokersList(response)
            } else {
                setNonMfBrokersList({} as GetNonMfBrokersListResponseBody)
            }
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        }
    }

    const fetchSettlementDetails = async (searchInput: string) => {
        try {
            if (searchInput?.trim()?.length !== 0) {
                const payload = {
                    security: formikRef?.current?.values?.security?.security ?? "",
                    searchInput: searchInput?.trim(),
                    product: activeProductType === "Unlisted Equity" ? "unListedEquity" : activeProductType?.toLowerCase()
                }

                const response = await (dispatch(getSettlementDetails(payload)) as unknown as Promise<GetSettlementDetailsResponseBody>);

                if (!response?.isSuccess) {
                    dispatch(showError(response?.message ?? "No Data Available"))
                }

                if (response?.isSuccess) {
                    setSettlementDetailsList(response)
                }
            } else {
                setSettlementDetailsList({} as GetSettlementDetailsResponseBody)
            }
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        }
    }

    const fetchCounterPartyList = async (searchInput: string) => {
        try {
            if (searchInput?.trim()?.length !== 0) {
                const payload = {
                    searchInput: searchInput?.trim()
                }

                const response = await (dispatch(getCounterPartyList(payload)) as unknown as Promise<GetCounterPartyListResponseBody>);

                setCounterPartyList(response)
            } else {
                setCounterPartyList({} as GetCounterPartyListResponseBody)
            }
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        }
    }

    const fetchHoldingCommitmentInfo = async (security: string, portfolio: string, folioNo: string) => {
        return await (dispatch(getHoldingCommitmentInfo({ security, portfolio, folioNo })) as unknown as Promise<GetHoldingCommitmentInfoResponseBody>);
    }

    const handleSuccessDialogClick = () => {
        history.push('/orders/order-dashboard')
        setFormSubmitStatus("")
    }

    const handleErrorDialogClick = () => {
        setFormSubmitStatus("")
    }

    const handleSubmit = async (values: Record<string, any>) => {
        try {
            setLoadingStatus("formSubmitting")

            const isAccountTypePoa = [
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.ADVISORY],
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.POA]
            ].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${values?.accountType ?? ""}`.toUpperCase()])

            let unlistedEquityTradeCalculation: TradeCalculationData | null = null

            if (activeProductType === "Unlisted Equity") {
                const payload = {
                    // TEMP CHANGES
                    // portfolioId: "TW100026",
                    // security: "887",
                    // quantity: Number(enteredValue ?? ""),
                    // price: 10, //Rate
                    // transactionType: "Buy"
                    // TEMP CHANGES

                    portfolioId: values?.account ?? "",
                    security: values?.security?.security ?? "",
                    quantity: values?.quantity ?? "",
                    price: Number(`${values?.rate ?? ""}`?.replaceAll(',', '')), //Rate
                    transactionType: values?.transactionType ?? ""
                }

                const response = await (dispatch(getTradeCalculation(payload)) as unknown as Promise<GetTradeCalculationResponseBody>);

                if (!response?.isSuccess) {
                    dispatch(showError(response?.message ?? "No Data Available"))
                    return
                }

                if (response?.isSuccess) {
                    unlistedEquityTradeCalculation = response?.tradeCalculationData ?? {}
                }
            }

            const selectedPortfolioDetails: PortfolioDetails = values
                ?.clientName
                ?.portfolios
                ?.find(
                    (obj: PortfolioDetails) =>
                        obj?.portfolioId === values?.account) ?? {} as PortfolioDetails

            const payload = {
                clientName: values?.clientName?.customerName ?? "", //Client Name
                portfolioName: selectedPortfolioDetails?.portfolioName ?? "", //Account
                accountType: values?.accountType ?? "", //Account Type
                investorBankAccount: values?.investorBankAccount?.bankAccNo ?? "", //Investor Bank Account
                bankSubAccountType: values?.bankSubAccountType ?? "",
                investorBankName: values?.investorBankAccount?.bankname ?? "",
                investorBankIfscCode: values?.investorBankAccount?.ifscCode ?? "",
                bankBalance: isAccountTypePoa ? `${values?.availableBankBalance ?? ""}`?.replaceAll(',', '') : "", //Available Bank Balance
                emailId: values?.emailId ?? "", //Email ID
                // orderNumber: values?.orderNumber ?? "", //Order Number
                orderDate: values?.orderDate?.hour(currentDate?.hour())?.minute(currentDate?.minute())?.second(currentDate?.second())?.format('YYYY-MM-DDTHH:mm:ss.SSSZ') ?? null, //Order Date
                transactionType: values?.transactionType ?? "", //Transaction Type
                rmRemarks: values?.remarks ?? "", // Remarks
                settlementMethod: values?.settlementMethod ?? "", //Settlement method
                settlementMethodCode: ORDER_ENTRY_SETTLEMENT_METHOD_CODE[values?.settlementMethod ?? ""] ?? "", //Settlement method Code
                settlementDate: values?.settlementDate?.hour(currentDate?.hour())?.minute(currentDate?.minute())?.second(currentDate?.second())?.format('YYYY-MM-DDTHH:mm:ss.SSSZ') ?? "", //Settlement Date
                referenceNumber: values?.referenceNumber ?? "", //Reference Number
                settlementBank: values?.settlementBank ?? "", //Settlement Bank
                settlementBankNumber: values?.settlementBankNumber?.bankAccNo ?? "", //Settlement Bank Number,
                settleBankIfscCode: `${values?.settlementBankNumber?.ifscCode ?? ""}`,
                settlementBankSubAccountType: `${values?.settlementBankNumber?.subAccountType ?? ""}`,
                documents: values?.documents,
                clientCode: values?.clientName?.clientCode ?? "",
                portfolioId: selectedPortfolioDetails?.portfolioId ?? "",
                rmId: values?.clientName?.rmId ?? "",
                product: activeProductType,
                company: userDetails?.company ?? "",
                ...(activeProductType === "Unlisted Equity" ? {
                    security: values?.security?.security ?? "", //Security
                    securityName: values?.security?.securityName ?? "", //Security Name
                    isinNo: values?.security?.isin ?? "", //ISIN
                    issuer: values?.issuer ?? "", //Issuer
                    counterParty: values?.counterParty?.identifier ?? "", //Counter Party
                    counterPartyName: values?.counterParty?.description ?? "", //Counter Party Name
                    quantity: values?.quantity ?? "", //Quantity
                    rate: `${values?.rate ?? ""}`?.replaceAll(',', ''), //Rate
                    stampDuty: values?.stampDuty ? "1" : "0", //Stamp Duty
                    charges: `${values?.charges ?? ""}`?.replaceAll(',', ''), //Charges
                    investmentAmount: `${values?.amount ?? ""}`?.replaceAll(',', ''), //Amount
                    depositoryName: values?.settlementDetailsDepositoryName ?? "", //Depository Name
                    dpName: values?.settlementDetailsDpName?.dpName ?? "", //DP Name
                    settlementDpId: values?.settlementDetailsDpId ?? "", //DP ID
                    clientId: values?.settlementDetailsClientId ?? "", //Client ID
                    // EXTRA fields
                    issuerName: values?.security?.issuerName ?? "",
                    netAmountPcy: `${unlistedEquityTradeCalculation?.netAmountPcy ?? ""}`,
                    portfolioCcy: `${selectedPortfolioDetails?.portfolioCcy ?? ""}`,
                    grossAmtPcy: `${unlistedEquityTradeCalculation?.grossAmtPcy ?? ""}`,
                    grossAmtTcy: `${unlistedEquityTradeCalculation?.grossAmtTcy ?? ""}`,
                    realGlPcy: `${unlistedEquityTradeCalculation?.realGlPcy ?? ""}`,
                    realGlTcy: `${unlistedEquityTradeCalculation?.realGlTcy ?? ""}`,
                    purchaseYield: `${unlistedEquityTradeCalculation?.purchaseYield ?? ""}`,
                    grossIntPcy: `${unlistedEquityTradeCalculation?.grossIntPcy ?? ""}`,
                    currency: `${values?.security?.currency ?? ""}`,
                    portfolioNo: `${selectedPortfolioDetails?.portfolioNo ?? ""}`,
                    exchange: `${selectedPortfolioDetails?.exchange ?? ""}`,
                    segment: `${values?.security?.segment ?? ""}`,
                    bankSource: SETTLEMENT_BANK_SOURCE_LIST.find(obj => obj.value === (values?.settlementBankSource ?? ""))?.key ?? "", // Settlement Bank Source
                    settlementBankAccCcy: `${values?.settlementBankNumber?.bankAccCcy ?? ""}`,
                    settlementBankAccCode: `${values?.settlementBankNumber?.bankAccCode ?? ""}`,
                    grossIntTcy: `${unlistedEquityTradeCalculation?.grossIntTcy ?? ""}`,
                    settlementBranch: `${values?.settlementBankNumber?.branch ?? ""}`,
                    payeeName: `${values?.settlementBankNumber?.bankAccHolder ?? ""}`
                    // EXTRA fields
                } : {}),
                ...(activeProductType === "AIF" ? {
                    security: values?.security?.security ?? "", //Security
                    securityName: values?.security?.securityName ?? "", //Security Name
                    folioNumber: values?.folioNumber ?? "", // Folio Number
                    issuer: values?.issuer ?? "", //Issuer
                    outstandingUnits: `${values?.selectedFolioData?.qtySaleable ?? values?.security?.qtySaleable ?? ""}`, //Outstanding Units — from selected folio
                    quantity: `${values?.quantity ?? ""}`, // Quantity
                    redeemType: values?.redeemType ?? "Quantity", // Redeem Type (Quantity or Amount)
                    lastAvailableNav: `${values?.lastAvailableNav ?? ""}`?.replaceAll(',', ''), //Last Available NAV
                    marketValue: `${values?.currentMarketValue ?? ""}`?.replaceAll(',', ''), //Current Market Value
                    commitmentAmount: `${values?.commitmentAmount ?? ""}`?.replaceAll(',', ''), //Commitment Amount
                    investmentAmount: `${values?.amount ?? ""}`?.replaceAll(',', ''), //Amount
                    deferral: values?.deferralException ?? "", // Deferral/Exception
                    depositoryName: values?.settlementDetailsDepositoryName ?? "", //Depository Name
                    dpName: values?.settlementDetailsDpName?.dpName ?? "", //DP Name
                    settlementDpId: values?.settlementDetailsDpId ?? "", //DP ID
                    clientId: values?.settlementDetailsClientId ?? "", //Client ID
                    // EXTRA fields
                    issuerName: values?.security?.issuerName ?? "",
                    settlementBankAccCcy: `${values?.settlementBankNumber?.bankAccCcy ?? ""}`,
                    settlementBankAccCode: `${values?.settlementBankNumber?.bankAccCode ?? ""}`,
                    currency: `${values?.security?.currency ?? ""}`,
                    portfolioCcy: `${selectedPortfolioDetails?.portfolioCcy ?? ""}`,
                    faceValue: `${values?.security?.faceValue ?? ""}`,
                    settlementBranch: `${values?.settlementBankNumber?.branch ?? ""}`,
                    payeeName: `${values?.settlementBankNumber?.bankAccHolder ?? ""}`
                    // EXTRA fields
                } : {}),
                ...(activeProductType === "PMS" ? {
                    security: values?.security?.security ?? "", //Security
                    securityName: values?.security?.securityName ?? "", //Security Name
                    folioNumber: values?.folioNumber ?? "", // Folio Number
                    issuer: values?.issuer ?? "", //Issuer
                    investmentAmount: `${values?.amount ?? ""}`?.replaceAll(',', ''), //Amount
                    currentPortfolioValue: `${values?.currentPortfolioValue ?? ""}`?.replaceAll(',', ''), // Current Portolfio Value
                    lastValuationDate: values?.lastValuationDate && typeof values?.lastValuationDate?.hour === 'function'
                        ? values?.lastValuationDate?.hour(currentDate?.hour())?.minute(currentDate?.minute())?.second(currentDate?.second())?.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
                        : "", //Last Valuation date
                    // EXTRA fields
                    issuerName: values?.security?.issuerName ?? "",
                    settlementBankAccCcy: `${values?.settlementBankNumber?.bankAccCcy ?? ""}`,
                    settlementBankAccCode: `${values?.settlementBankNumber?.bankAccCode ?? ""}`,
                    currency: `${values?.security?.currency ?? ""}`,
                    portfolioCcy: `${selectedPortfolioDetails?.portfolioCcy ?? ""}`,
                    faceValue: `${values?.security?.faceValue ?? ""}`,
                    settlementBranch: `${values?.settlementBankNumber?.branch ?? ""}`,
                    payeeName: `${values?.settlementBankNumber?.bankAccHolder ?? ""}`
                    // EXTRA fields
                } : {}),
                ...(activeProductType === "Bonds" ? {
                    uccCode: values?.uccCode ?? "", //UCC Code
                    // dpId: values?.dpId ?? "", //DP ID
                    security: values?.security?.security ?? "", //Security
                    securityName: values?.security?.securityName ?? "", //Security Name
                    issuer: values?.issuer ?? "", //Issuer
                    quantity: values?.quantity ?? "", //Quantity
                    priceType: PRICE_TYPE_DROPDOWN_LIST.find(obj => obj.value === (values?.priceType ?? ""))?.key ?? "", //Price Type 
                    cleanPrice: `${values?.cleanPrice ?? ""}`?.replaceAll(',', ''), //Clean Price
                    acquisitionCost: `${values?.acquisitionCost ?? ""}`?.replaceAll(',', ''), //Acquisition Cost
                    stampDuty: `${values?.stampDuty ?? ""}`?.replaceAll(',', ''), //Stamp Duty
                    accuredInterest: `${values?.accruedInterest ?? ""}`?.replaceAll(',', ''), //Accured Interest
                    investmentAmount: `${values?.settlementAmount ?? ""}`?.replaceAll(',', ''), //Settlement Amount
                    outstandingQuantity: values?.outstandingQuantity ?? "", //Outstanding Quantity
                    depositoryName: values?.settlementDetailsDepositoryName ?? "", //Depository Name
                    dpName: values?.settlementDetailsDpName?.dpName ?? "", //DP Name
                    settlementDpId: values?.settlementDetailsDpId ?? "", //DP ID
                    clientId: values?.settlementDetailsClientId ?? "", //Client ID
                    // EXTRA fields
                    issuerName: values?.security?.issuerName ?? "",
                    // broker: values?.broker?.identifier ?? "",
                    assetClass: values?.security?.assetClass ?? "",
                    counterParty: values?.counterParty?.identifier ?? "",
                    counterPartyName: values?.counterParty?.description ?? "", //Counter Party Name
                    netAmountPcy: `${values?.tradeCalculationData?.netAmountPcy ?? ""}`,
                    portfolioCcy: `${selectedPortfolioDetails?.portfolioCcy ?? ""}`,
                    acquCostTcy: `${values?.tradeCalculationData?.acquCostTcy ?? ""}`,
                    acquCostPcy: `${values?.tradeCalculationData?.acquCostPcy ?? ""}`,
                    grossAmtPcy: `${values?.tradeCalculationData?.grossAmtPcy ?? ""}`,
                    grossAmtTcy: `${values?.tradeCalculationData?.grossAmtTcy ?? ""}`,
                    realGlPcy: `${values?.tradeCalculationData?.realGlPcy ?? ""}`,
                    realGlTcy: `${values?.tradeCalculationData?.realGlTcy ?? ""}`,
                    purchaseYield: `${values?.tradeCalculationData?.purchaseYield ?? ""}`,
                    grossIntPcy: `${values?.tradeCalculationData?.grossIntPcy ?? ""}`,
                    currency: `${values?.security?.currency ?? ""}`,
                    portfolioNo: `${selectedPortfolioDetails?.portfolioNo ?? ""}`,
                    segment: `${values?.security?.segment ?? ""}`,
                    exchange: `${selectedPortfolioDetails?.exchange ?? ""}`,
                    bankSource: SETTLEMENT_BANK_SOURCE_LIST.find(obj => obj.value === (values?.settlementBankSource ?? ""))?.key ?? "", // Settlement Bank Source
                    settlementBankAccCcy: `${values?.settlementBankNumber?.bankAccCcy ?? ""}`,
                    settlementBankAccCode: `${values?.settlementBankNumber?.bankAccCode ?? ""}`,
                    grossIntTcy: `${values?.tradeCalculationData?.grossIntTcy ?? ""}`,
                    settlementBranch: `${values?.settlementBankNumber?.branch ?? ""}`,
                    payeeName: `${values?.settlementBankNumber?.bankAccHolder ?? ""}`
                    // EXTRA fields
                } : {}),
                ...(activeProductType === "Margin Money" ? {
                    investmentAmount: `${values?.amount ?? ""}`?.replaceAll(',', ''), //Amount
                    broker: values?.broker?.identifier ?? "", //Broker
                    brokerName: values?.broker?.description ?? "", //Broker Name
                    // EXTRA fields
                    portfolioCcy: `${selectedPortfolioDetails?.portfolioCcy ?? ""}`,
                    settlementBranch: `${values?.settlementBankNumber?.bankBranch ?? ""}`,
                    payeeName: `${values?.settlementBankNumber?.beneficiaryName ?? ""}`
                    // EXTRA fields
                } : {}),
                ...(activeProductType === "Corpus In/Out" ? {
                    investmentAmount: `${values?.amount ?? ""}`?.replaceAll(',', ''), //Amount
                    // EXTRA fields
                    portfolioCcy: `${selectedPortfolioDetails?.portfolioCcy ?? ""}`,
                    settlementBranch: `${values?.settlementBankNumber?.branchName ?? ""}`,
                    payeeName: `${values?.settlementBankNumber?.bankAccHolder ?? ""}`
                    // EXTRA fields
                } : {}),
                ...(activeProductType === "Fee Payment" ? {
                    investmentAmount: `${values?.amount ?? ""}`?.replaceAll(',', ''), //Amount
                    // EXTRA fields
                    portfolioCcy: `${selectedPortfolioDetails?.portfolioCcy ?? ""}`,
                    settlementBranch: `${values?.settlementBankNumber?.bankBranch ?? ""}`,
                    payeeName: `${values?.settlementBankNumber?.beneficiaryName ?? ""}`
                    // EXTRA fields
                } : {})
            }

            const response = await (dispatch(addOrderEntry(payload)) as unknown as Promise<AddOrderEntryResponseBody>);

            setFormSubmitStatusObj(response)

            if (response?.isSuccess) {
                setFormSubmitStatus("formSubmitSuccess")
            }

            if (!response?.isSuccess) {
                setFormSubmitStatus("formSubmitError")
            }

        } catch (error) {
            console.log(error ?? "")
        } finally {
            setLoadingStatus("")
        }
    }

    const handleDisableTransactDialogOpen = () => {
        setDisableTransactDialogVisible(true)
    }

    const handleDisableTransactDialogClose = async () => {
        setDisableTransactDialogVisible(false)

        formikRef?.current?.resetForm({
            values: {
                ...(formikRef?.current?.values ?? {}),
                account: "",
                accountType: "",
                modeOfHolding: "",
                emailId: "",
                settlementMethod: ""
            }
        })
    }

    const fetchAdvisoryBankDetails = async () => {
        try {
            setLoadingStatus("apiLoading")

            const response = await (dispatch(getAdvisoryBankDetails()) as unknown as Promise<GetAdvisoryBankDetailsResponseBody>);

            if (response) {
                return [response]
            }

            return []
        } catch (error) {
            console.log(error);
            return []
        } finally {
            setLoadingStatus("")
        }
    }

    const handleProductChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedProductValue = (event.target as HTMLInputElement).value as ProductType

        setActiveProductType(selectedProductValue);

        // TODO (SAN-06): dpId fetch via fetchBondsDpId is temporarily disabled. Re-enable when DP ID prefill for Bonds is required.
        // Remove fetchBondsDpId and let dpIdApiResponse if permanently removed.
        // let dpIdApiResponse = ""
        let advisoryBankDetailsResponse: Array<GetAdvisoryBankDetailsResponseBody> = []

        // if (selectedProductValue === "Bonds" && formikRef?.current?.values?.account) {
        //     const response = await fetchBondsDpId(formikRef?.current?.values?.account)

        //     dpIdApiResponse = response
        // }

        // Calling fetchAdvisoryBankDetails function to get Advisory Bank Details data for Fee Payment Product Type
        if (selectedProductValue === "Fee Payment") {
            const response = await fetchAdvisoryBankDetails()

            advisoryBankDetailsResponse = response ?? []
        }


        setInitialValues({
            ...baseInitialValues[selectedProductValue],
            // Common Client Details
            clientName: formikRef?.current?.values?.clientName,
            account: formikRef?.current?.values?.account ?? "",
            accountType: formikRef?.current?.values?.accountType ?? "",
            modeOfHolding: formikRef?.current?.values?.modeOfHolding ?? "",
            investorBankAccount: formikRef?.current?.values?.investorBankAccount,
            bankSubAccountType: formikRef?.current?.values?.bankSubAccountType,
            emailId: formikRef?.current?.values?.emailId ?? "",
            srm: formikRef?.current?.values?.srm ?? "",
            availableBankBalance: formikRef?.current?.values?.availableBankBalance ?? "",
            utilizedBalance: formikRef?.current?.values?.utilizedBalance ?? "",
            netAvailableBalance: formikRef?.current?.values?.netAvailableBalance ?? "",

            ...(selectedProductValue === "Bonds" ? {
                // In case of Bonds if we have selected Client name in other asset types, then when we come to Bonds the UCC code should be prefilled

                // Client Details
                uccCode: formikRef?.current?.values?.clientName?.value ?? "",
                // dpId: dpIdApiResponse
            } : {}),

            // Common Order Details
            // orderNumber: values?.orderNumber ?? "",
            // NOTE (SAN-07): orderDate is intentionally reset to businessDate on every product switch.
            // This ensures a fresh, valid date for the new product type rather than carrying over a potentially invalid date.
            // NOTE (CORR-19): expression is complete — dayjs().startOf('day') is valid. Reviewer's diff was truncated.
            // orderDate: formikRef?.current?.values?.orderDate ?? "",
            orderDate: businessDate ? dayjs(businessDate, 'DD/MM/YYYY') : dayjs().startOf('day'),
            remarks: formikRef?.current?.values?.remarks ?? "",

            // Common Settlement Details
            // settlementMethod: formikRef?.current?.values?.settlementMethod ?? "",
            settlementDate: formikRef?.current?.values?.settlementDate ?? "",
            referenceNumber: formikRef?.current?.values?.referenceNumber ?? "",
            // Adding advisoryBankDetails to Formik initial values in case of Fee Payment Product Type
            ...(selectedProductValue === "Fee Payment" ? {
                advisoryBankDetails: advisoryBankDetailsResponse
            } : {}),

            //Common Order Supporting Document
            documents: formikRef?.current?.values?.documents ?? [{
                documentId: "",
                documentType: ""
            },
            {
                documentId: "",
                documentType: ""
            }],

        })
        setValidations(({
            ...baseValidations[selectedProductValue],
            ...(businessDate ? {
                orderDate: ["Unlisted Equity", "Bonds"].includes(selectedProductValue)
                    ? getTMinus5OrderDateValidation(businessDate)
                    : yup
                        .date()
                        .required("Order Date is required")
                        .max(dayjs(businessDate, "DD/MM/YYYY").toDate(), `Order Date cannot be greater than ${businessDate}`),
                settlementDate: yup
                    .date()
                    .required("Settlement Date is required")
                    .min(dayjs(businessDate, "DD/MM/YYYY").toDate(), `Settlement Date cannot be less than ${businessDate}`)
            } : {})
        }))

        if ((
            selectedProductValue === "Margin Money" ||
            selectedProductValue === "Corpus In/Out" ||
            selectedProductValue === "Fee Payment"
        ) &&
            formikRef?.current?.values?.accountType?.length &&
            !([
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.ADVISORY],
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.POA]
            ].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${formikRef?.current?.values?.accountType ?? ""}`.toUpperCase()]))
        ) {
            handleDisableTransactDialogOpen()
        }
    }

    // Scenarios to call getBankBalance api
    // 1. When user selects the Client Name, if the account Type is ADVISORY/POA, call the getBankBalance api otherwise availableBankBalance is "Not Applicable"
    // 2. When user selects the Client Name and there are two accounts, one ADVISORY/POA and other Non ADVISORY/Non POA, when the user toggles between them, for ADVISORY/POA call the getBankBalance api, for Non ADVISORY/Non POA availableBankBalance is "Not Applicable" 
    // 3. When the user changes the bank account, if the account Type is ADVISORY/POA, call the getBankBalance api otherwise availableBankBalance is "Not Applicable" 

    const handleClientNameChange = async (selectedObj: Record<string, any>) => {
        try {
            if (!selectedObj) {
                setUploadedFileArray([[]])

                formikRef?.current?.resetForm({
                    values: {
                        ...initialValues,
                        clientName: null,
                        account: "",
                        accountType: "",
                        modeOfHolding: "",
                        investorBankAccount: null,
                        bankSubAccountType: "",
                        availableBankBalance: "",
                        utilizedBalance: "",
                        netAvailableBalance: "",
                        emailId: "",
                        ...(activeProductType === "Bonds" ? {
                            // In case of Bonds resetting below fields
                            // Client Details
                            uccCode: "",
                            // dpId: "",
                        } : {}),
                        srm: "",
                        orderDate: businessDate ? dayjs(businessDate, 'DD/MM/YYYY') : dayjs().startOf('day'),
                        remarks: "",
                        // settlementMethod: "",
                        settlementDate: businessDate ? dayjs(businessDate, 'DD/MM/YYYY') : dayjs().startOf('day'),
                        referenceNumber: "",
                        documents: [{
                            documentId: "",
                            documentType: ""
                        }]
                    }
                })

                return
            }

            setLoadingStatus("apiLoading")

            await formikRef?.current?.setFieldValue('clientName', selectedObj)

            // Bank Lists API start
            const bankListPayload = {
                clientCode: selectedObj?.value ?? ""
            }

            const getOrderEntryBanksListResponse = await (dispatch(getOrderEntryBanksList(bankListPayload)) as unknown as Promise<GetOrderEntryBanksListResponseBody>);

            setBankAccountList(getOrderEntryBanksListResponse)

            const defaultBankObj = (getOrderEntryBanksListResponse?.bankDetails ?? [])?.find((obj) => obj.isDefaultBankAc?.toLowerCase() === "y");

            const defaultBankAccountObj = defaultBankObj ? defaultBankObj : getOrderEntryBanksListResponse?.bankDetails?.[0]
            // Bank Lists API end

            const isAccountTypePoa = [
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.ADVISORY],
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.POA]
            ].includes(MPOWER_ACCOUNT_TYPE_MASTER[((selectedObj?.portfolios ?? [])?.[0]?.accountType ?? "")?.toUpperCase()])

            // Bank Balance API start

            let getBankBalanceResponse: GetBankBalanceResponseBody | null = null

            if (isAccountTypePoa) {
                getBankBalanceResponse = await (dispatch(getBankBalance({
                    investorBankAccount: defaultBankAccountObj?.bankAccNo ?? "",
                    clientCode: `${selectedObj?.clientCode ?? ""}`,
                    investorIfscCode: `${defaultBankAccountObj?.ifscCode ?? ""}`
                })) as unknown as Promise<GetBankBalanceResponseBody>);

                if (!getBankBalanceResponse?.isSuccess) {
                    dispatch(showError(getBankBalanceResponse?.message ?? "No Data Available"))
                }
            }
            // Bank Balance API end

            // In case of Bonds calling the DpId API start
            // let dpIdApiResponse = ""

            // if (activeProductType === "Bonds") {
            //     const fetchBondsDpIdResponse = await fetchBondsDpId((selectedObj?.portfolios ?? [])?.[0]?.portfolioId ?? "")

            //     dpIdApiResponse = fetchBondsDpIdResponse
            // }
            // In case of Bonds calling the DpId API end

            if ((
                activeProductType === "Margin Money" ||
                activeProductType === "Corpus In/Out" ||
                activeProductType === "Fee Payment"
            ) &&
                (selectedObj?.portfolios ?? [])?.[0]?.accountType?.length &&
                !([
                    MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.ADVISORY],
                    MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.POA]
                ].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${(selectedObj?.portfolios ?? [])?.[0]?.accountType ?? ""}`.toUpperCase()]))
            ) {
                handleDisableTransactDialogOpen()
            }

            setUploadedFileArray([[]])

            formikRef?.current?.resetForm({
                values: {
                    ...initialValues,
                    clientName: selectedObj,
                    account: (selectedObj?.portfolios ?? [])?.[0]?.portfolioId ?? "",
                    accountType: (selectedObj?.portfolios ?? [])?.[0]?.accountType ?? "",
                    modeOfHolding: (selectedObj?.portfolios ?? [])?.[0]?.modeOfHolding ?? "",
                    investorBankAccount: defaultBankAccountObj ?? null,
                    bankSubAccountType: defaultBankAccountObj?.subAccountType ?? "",
                    availableBankBalance: isAccountTypePoa ?
                        (!Number.isNaN(Number(getBankBalanceResponse?.bankBalance ?? 0)) ? Number(getBankBalanceResponse?.bankBalance ?? 0) : 0)?.toLocaleString(locale)
                        : "Not Applicable",
                    utilizedBalance: isAccountTypePoa ?
                        (!Number.isNaN(Number(getBankBalanceResponse?.utilizedAmount ?? 0)) ? Number(getBankBalanceResponse?.utilizedAmount ?? 0) : 0)?.toLocaleString(locale)
                        : "Not Applicable",
                    netAvailableBalance: isAccountTypePoa ?
                        (!Number.isNaN(Number(getBankBalanceResponse?.netAmount ?? 0)) ? Number(getBankBalanceResponse?.netAmount ?? 0) : 0)?.toLocaleString(locale)
                        : "Not Applicable",
                    emailId: (selectedObj?.portfolios ?? [])?.[0]?.email ?? "",
                    ...(activeProductType === "Bonds" ? {
                        // In case of Bonds if we have NOT selected Client name in other asset types, then when we come to Bonds and select client name the UCC code should be prefilled
                        // Client Details
                        uccCode: selectedObj?.value ?? "",
                        // dpId: dpIdApiResponse ?? "",
                    } : {}),
                    srm: `${selectedObj?.rmName ? selectedObj?.rmName + " (" + (selectedObj?.rmId ?? "") + ")" : ""}`,
                    orderDate: businessDate ? dayjs(businessDate, 'DD/MM/YYYY') : dayjs().startOf('day'),
                    remarks: "",
                    // settlementMethod: "",
                    settlementDate: businessDate ? dayjs(businessDate, 'DD/MM/YYYY') : dayjs().startOf('day'),
                    referenceNumber: "",
                    documents: [{
                        documentId: "",
                        documentType: ""
                    }]
                }
            })

        } catch (error) {
            console.log((error as Error)?.message ?? "");
        } finally {
            setLoadingStatus("")
        }
    }

    const handleAccountChange = async (event: SelectChangeEvent<unknown>) => {
        // let dpIdApiResponse = ""

        // if (activeProductType === "Bonds") {
        //     const response = await fetchBondsDpId(event.target.value as string)

        //     dpIdApiResponse = response
        // }

        const selectedAccount = (formikRef?.current?.values?.clientName?.portfolios ?? [])?.find((obj: PortfolioDetails) => obj?.portfolioId === event.target.value)

        const isAccountTypePoa = [
            MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.ADVISORY],
            MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.POA]
        ].includes(MPOWER_ACCOUNT_TYPE_MASTER[selectedAccount?.accountType ?? ""])

        // Bank Balance API start

        let getBankBalanceResponse: GetBankBalanceResponseBody | null = null

        if (isAccountTypePoa) {
            getBankBalanceResponse = await (dispatch(getBankBalance({
                investorBankAccount: formikRef?.current?.values?.investorBankAccount?.bankAccNo ?? "",
                clientCode: `${formikRef?.current?.values?.clientName?.clientCode ?? ""}`,
                investorIfscCode: `${formikRef?.current?.values?.investorBankAccount?.ifscCode ?? ""}`
            })) as unknown as Promise<GetBankBalanceResponseBody>);

            if (!getBankBalanceResponse?.isSuccess) {
                dispatch(showError(getBankBalanceResponse?.message ?? "No Data Available"))
            }
        }
        // Bank Balance API end

        setUploadedFileArray([[]])

        formikRef?.current?.resetForm({
            values: {
                ...initialValues,
                clientName: formikRef?.current?.values?.clientName,
                account: event.target.value,
                accountType: selectedAccount?.accountType ?? "",
                modeOfHolding: selectedAccount?.modeOfHolding ?? "",
                investorBankAccount: formikRef?.current?.values?.investorBankAccount,
                bankSubAccountType: formikRef?.current?.values?.bankSubAccountType,
                availableBankBalance: isAccountTypePoa ?
                    (!Number.isNaN(Number(getBankBalanceResponse?.bankBalance ?? 0)) ? Number(getBankBalanceResponse?.bankBalance ?? 0) : 0)?.toLocaleString(locale)
                    : "Not Applicable",
                utilizedBalance: isAccountTypePoa ?
                    (!Number.isNaN(Number(getBankBalanceResponse?.utilizedAmount ?? 0)) ? Number(getBankBalanceResponse?.utilizedAmount ?? 0) : 0)?.toLocaleString(locale)
                    : "Not Applicable",
                netAvailableBalance: isAccountTypePoa ?
                    (!Number.isNaN(Number(getBankBalanceResponse?.netAmount ?? 0)) ? Number(getBankBalanceResponse?.netAmount ?? 0) : 0)?.toLocaleString(locale)
                    : "Not Applicable",
                emailId: selectedAccount?.email ?? "",
                ...(activeProductType === "Bonds" ? {
                    uccCode: formikRef?.current?.values?.uccCode ?? "",
                    // dpId: dpIdApiResponse
                } : {}),
                srm: formikRef?.current?.values?.srm ?? "",
                orderDate: businessDate ? dayjs(businessDate, 'DD/MM/YYYY') : dayjs().startOf('day'),
                remarks: "",
                // settlementMethod: "",
                settlementDate: businessDate ? dayjs(businessDate, 'DD/MM/YYYY') : dayjs().startOf('day'),
                referenceNumber: "",
                documents: [{
                    documentId: "",
                    documentType: ""
                }]
            }
        })

        if ((
            activeProductType === "Margin Money" ||
            activeProductType === "Corpus In/Out" ||
            activeProductType === "Fee Payment"
        ) &&
            (selectedAccount?.accountType ?? "")?.length &&
            !([
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.ADVISORY],
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.POA]
            ].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${selectedAccount?.accountType ?? ""}`.toUpperCase()]))
        ) {
            handleDisableTransactDialogOpen()
        }
    }

    return (
        <>
            <Backdrop
                sx={(theme: Theme) => ({
                    color: theme.palette.common.white,
                    zIndex: theme.zIndex.modal + 1
                })}
                open={["apiLoading", "formSubmitting"].includes(loadingStatus)}
            >
                <CircularProgress sx={(theme: Theme) => ({ color: theme.palette.primary[500] })} />
            </Backdrop>
            <Box>
                <Typography
                    variant="h1"
                    sx={(theme: Theme) => ({
                        lineHeight: "32.5px",
                        color: theme.palette.text.primary
                    })}
                >
                    Order Details
                </Typography>
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={yup.object().shape(validations)}
                    enableReinitialize
                    validateOnChange={false}
                >
                    {() => {
                        return (
                            <Form autoComplete='off'>
                                <Box
                                    sx={(theme: Theme) => ({
                                        marginTop: "15.5px",
                                        borderRadius: "25px",
                                        padding: "25px",
                                        backgroundColor: theme.palette.common.white,
                                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                                    })}
                                >
                                    <Typography
                                        variant="h2"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "25px",
                                            letterSpacing: "0.01px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        Client Details
                                    </Typography>
                                    <Box sx={{ marginTop: "28px" }}>
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.text.secondary
                                            })}
                                        >
                                            Product
                                        </Typography>
                                        <FormControl sx={{ marginTop: "15px" }}>
                                            <RadioGroup
                                                row
                                                value={activeProductType}
                                                onChange={handleProductChange}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-start',
                                                    alignItems: 'center',
                                                    rowGap: "10px"
                                                }}
                                            >
                                                {productTypeList.map(({ productLabel, productValue }) => (
                                                    <FormControlLabel
                                                        key={productValue}
                                                        value={productValue}
                                                        control={
                                                            <Radio
                                                                size="medium"
                                                                sx={(theme: Theme) => ({
                                                                    color: theme.palette.primary[500],
                                                                    "&.MuiRadio-root": {
                                                                        padding: "0px",
                                                                    },
                                                                    ...(activeProductType !== productValue ? {
                                                                        stroke: theme.palette.primary[50],
                                                                    } : {}),
                                                                    "&.Mui-disabled": {
                                                                        color: `${theme.palette.text.secondary}B3`,
                                                                        opacity: 0.5
                                                                    }
                                                                })}
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography
                                                                    component="span"
                                                                    variant="body_500"
                                                                    sx={(theme: Theme) => ({
                                                                        lineHeight: "20px",
                                                                        letterSpacing: '0.02px',
                                                                        color: activeProductType === productValue ?
                                                                            theme.palette.primary[500] :
                                                                            theme.palette.text.primary
                                                                    })}
                                                                >
                                                                    {productLabel}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        sx={(theme: Theme) => ({
                                                            borderRadius: '10px',
                                                            backgroundColor: activeProductType === productValue ?
                                                                theme.palette.customColors.darkGrey :
                                                                `${theme.palette.common.white}0D`,
                                                            padding: activeProductType === productValue ? '7px 12px' : '8px 12px',
                                                            border: activeProductType === productValue ?
                                                                `1px solid ${theme.palette.primary[500]}` :
                                                                `0.5px solid ${theme.palette.text.secondary}80`,
                                                            "&.MuiFormControlLabel-root": {
                                                                columnGap: "10px",
                                                                marginLeft: "0px",
                                                                marginRight: "12px"
                                                            },
                                                            cursor: 'pointer',
                                                            "&.Mui-disabled": {
                                                                pointerEvents: "auto",
                                                                cursor: "not-allowed",
                                                                ".MuiTypography-root": {
                                                                    color: `${theme.palette.text.secondary}B3`
                                                                },
                                                            }
                                                        })}
                                                        disabled={
                                                            ["formSubmitting", "apiLoading"].includes(loadingStatus)
                                                        }
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>
                                    <Box sx={{ marginTop: "32px" }}>
                                        <ClientDetailsForm
                                            investorData={investorData}
                                            activeProductType={activeProductType}
                                            handleClientNameChange={handleClientNameChange}
                                            bankAccountList={bankAccountList}
                                            handleAccountChange={handleAccountChange}
                                            setLoadingStatus={setLoadingStatus}
                                            isLoading={["apiLoading", "formSubmitting"].includes(loadingStatus)}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    sx={(theme: Theme) => ({
                                        marginTop: "15px",
                                        borderRadius: "25px",
                                        padding: "25px",
                                        backgroundColor: theme.palette.common.white,
                                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                                    })}
                                >
                                    <Typography
                                        variant="h2"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "25px",
                                            letterSpacing: "0.01px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        Order Details
                                    </Typography>
                                    <Box sx={{ marginTop: "28px" }}>
                                        <OrderDetailsForm
                                            productType={activeProductType}
                                            securitiesData={securitiesData}
                                            fetchSecuritiesData={fetchSecuritiesData}
                                            nonMfBrokersList={nonMfBrokersList}
                                            fetchNonMfBrokers={fetchNonMfBrokers}
                                            setValidations={setValidations}
                                            counterPartyList={counterPartyList}
                                            fetchCounterPartyList={fetchCounterPartyList}
                                            isLoading={["apiLoading", "formSubmitting"].includes(loadingStatus)}
                                            setLoadingStatus={setLoadingStatus}
                                            fetchHoldingCommitmentInfo={fetchHoldingCommitmentInfo}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    sx={(theme: Theme) => ({
                                        marginTop: "15px",
                                        borderRadius: "25px",
                                        padding: "25px",
                                        backgroundColor: theme.palette.common.white,
                                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                                    })}
                                >
                                    <Typography
                                        variant="h2"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "25px",
                                            letterSpacing: "0.01px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        Settlement Details
                                    </Typography>
                                    <Box
                                        sx={(theme: Theme) => ({
                                            marginTop: "28px",
                                            padding: "25px 0px",
                                            borderTop: `1px solid ${theme.palette.customColors.pastelGrey}`,
                                            borderBottom: `1px solid ${theme.palette.customColors.pastelGrey}`
                                        })}
                                    >
                                        <SettlementDetailsForm
                                            fetchSettlementDetails={fetchSettlementDetails}
                                            settlementDetailsList={settlementDetailsList}
                                            activeProductType={activeProductType}
                                            setValidations={setValidations}
                                            bankAccountList={bankAccountList}
                                            isLoading={["apiLoading", "formSubmitting"].includes(loadingStatus)}
                                            setLoadingStatus={setLoadingStatus}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h2"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "25px",
                                            letterSpacing: "0.01px",
                                            color: theme.palette.text.primary,
                                            marginTop: "25px"
                                        })}
                                    >
                                        Order Supporting Document
                                    </Typography>
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: `${theme.palette.text.secondary}B2`,
                                            marginTop: "10px"
                                        })}
                                    >
                                        You can add minimum 1 attachment and maximum 5 attachments
                                    </Typography>
                                    <OrderSupportingDocumentForm
                                        setLoadingStatus={setLoadingStatus}
                                        uploadedFileArray={uploadedFileArray}
                                        setUploadedFileArray={setUploadedFileArray}
                                        isLoading={["apiLoading", "formSubmitting"].includes(loadingStatus)}
                                    />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            marginTop: "28px",
                                            justifyContent: "center",
                                            alignItems: "center"
                                        }}
                                    >
                                        <LoadingButton
                                            type="button"
                                            onClick={async () => {
                                                const errors = await formikRef?.current?.validateForm()
                                                if (errors && Object.keys(errors).length > 0) {
                                                    console.log('[PMS/AIF Debug] Validation errors blocking submit:', JSON.stringify(errors, null, 2))
                                                }
                                                formikRef?.current?.submitForm()
                                            }}
                                            variant="contained"
                                            disabled={["apiLoading"].includes(loadingStatus)}
                                            loading={["formSubmitting"].includes(loadingStatus)}
                                            loadingPosition="center"
                                            sx={(theme: Theme) => ({
                                                ...theme.typography.body_500,
                                                fontWeight: 700,
                                                lineHeight: "22.5px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.textDark.primary,
                                                borderRadius: "10px",
                                                padding: "13px 56px",
                                                boxShadow: "none",
                                                backgroundColor: theme.palette.primary[500],
                                                width: "50%"
                                            })}
                                        >
                                            Submit
                                        </LoadingButton>
                                    </Box>
                                </Box>
                            </Form>
                        )
                    }}
                </Formik>
            </Box>

            {/* Success Dialog Start */}
            <Dialog
                aria-labelledby="success-dialog"
                open={formSubmitStatus === "formSubmitSuccess"
                }
                PaperProps={{
                    sx: (theme: Theme) => ({
                        maxWidth: '550px',
                        width: "100%",
                        borderRadius: "12px",
                        padding: "24px",
                        backgroundColor: theme.palette.common.white
                    }),
                }}
            >
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        rowGap: "12px",
                        padding: "15px",
                        overflow: "hidden"
                    }}
                >
                    <SuccessIcon />
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary
                        })}
                    >
                        Success!
                    </Typography>
                    <Typography
                        variant="subtitle_400"
                        sx={(theme: Theme) => ({
                            lineHeight: "22px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.secondary,
                            textAlign: "center",
                            paddingLeft: "10px",
                            paddingRight: "10px"
                        })}
                    >
                        {formSubmitStatusObj?.message ?? ""}
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: "0px",
                        marginTop: "40px",
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSuccessDialogClick}
                        sx={(theme: Theme) => ({
                            ...theme.typography.body_500,
                            fontWeight: 600,
                            lineHeight: "20px",
                            color: theme.palette.common.white,
                            borderRadius: "4px",
                            padding: "13px 20px",
                            backgroundColor: theme.palette.primary[500]
                        })}
                    >
                        Go to Order Dashboard
                    </Button>
                </DialogActions>
            </Dialog >
            {/* Success Dialog End */}

            {/* Error Dialog Start */}
            <Dialog
                aria-labelledby="error-dialog"
                open={formSubmitStatus === "formSubmitError"}
                PaperProps={{
                    sx: (theme: Theme) => ({
                        maxWidth: '550px',
                        width: "100%",
                        borderRadius: "12px",
                        padding: "24px",
                        backgroundColor: theme.palette.common.white
                    }),
                }}
            >
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        rowGap: "12px",
                        padding: "15px",
                        overflow: "hidden"
                    }}
                >
                    <FailureIcon width="35" height="35" />
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary
                        })}
                    >
                        Error!
                    </Typography>
                    <Typography
                        variant="subtitle_400"
                        sx={(theme: Theme) => ({
                            lineHeight: "22px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.secondary,
                            textAlign: "center",
                            paddingLeft: "10px",
                            paddingRight: "10px"
                        })}
                    >
                        {formSubmitStatusObj?.message ?? ""}
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: "0px",
                        marginTop: "40px",
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleErrorDialogClick}
                        sx={(theme: Theme) => ({
                            ...theme.typography.body_500,
                            fontWeight: 600,
                            lineHeight: "20px",
                            color: theme.palette.common.white,
                            borderRadius: "4px",
                            padding: "13px 20px",
                            backgroundColor: theme.palette.primary[500]
                        })}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Error Dialog End */}

            {/* Disable Transact Dialog Start */}
            <Dialog
                aria-labelledby="error-dialog"
                open={isDisableTransactDialogVisible}
                PaperProps={{
                    sx: (theme: Theme) => ({
                        maxWidth: '550px',
                        width: "100%",
                        borderRadius: "12px",
                        padding: "24px",
                        backgroundColor: theme.palette.common.white
                    }),
                }}
            >
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        rowGap: "17px",
                        padding: "0px 15px",
                        overflow: "hidden"
                    }}
                >
                    <FailureIcon width="35" height="35" />
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            lineHeight: "22px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.secondary,
                            textAlign: "center",
                            paddingLeft: "10px",
                            paddingRight: "10px",
                        })}
                    >
                        Transaction Type is applicable only for Advisory investors
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: "0px",
                        marginTop: "40px",
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleDisableTransactDialogClose}
                        sx={(theme: Theme) => ({
                            ...theme.typography.body_500,
                            fontWeight: 600,
                            lineHeight: "20px",
                            color: theme.palette.common.white,
                            borderRadius: "4px",
                            padding: "13px 20px",
                            backgroundColor: theme.palette.primary[500]
                        })}
                    >
                        Okay
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Disable Transact Dialog End */}
        </>
    )
}